import { jsPDF } from "jspdf";
import QRCode from "qrcode";

const PAGE_W = 200; // mm
const PAGE_H = 100; // mm

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/**
 * Render a single ticket onto an existing jsPDF page.
 * Layout (200x100 mm landscape):
 *
 *  +-------+----------------------------------------+
 *  | STUB  | EVENT NAME                  +--------+ |
 *  | #N    | Venue                       |   QR   | |
 *  | code  | Start                       |        | |
 *  |       | End                         +--------+ |
 *  |       | URL                            CODE    |
 *  |       | -------------------------------------- |
 *  |       | Present this QR at entrance.           |
 *  +-------+----------------------------------------+
 */
async function renderTicket(doc, { event, ticket, publicUrl }) {
  // Outer border
  const M = 4; // outer margin
  doc.setDrawColor(18, 18, 18);
  doc.setLineWidth(0.8);
  doc.rect(M, M, PAGE_W - M * 2, PAGE_H - M * 2);

  // ----- Stub (left black panel) -----
  const stubW = 36;
  doc.setFillColor(18, 18, 18);
  doc.rect(M, M, stubW, PAGE_H - M * 2, "F");

  // "// TICKET"
  doc.setTextColor(255, 204, 0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("// TICKET", M + 4, M + 8);

  // "#N"
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(34);
  doc.text(`#${ticket.serial}`, M + 4, M + 28);

  // short code
  doc.setFont("courier", "normal");
  doc.setFontSize(8);
  doc.setTextColor(200, 200, 200);
  doc.text(ticket.code, M + 4, PAGE_H - M - 6);

  // ----- Body (right panel) -----
  const bodyX = M + stubW + 6;
  const bodyW = PAGE_W - bodyX - M - 4;

  // QR — bottom-right, fixed size
  const qrSize = 50;
  const qrX = PAGE_W - M - qrSize - 4;
  const qrY = M + 4;

  // Reserve text area to the left of the QR (with 4mm gap)
  const textRight = qrX - 4;
  const textWidth = textRight - bodyX;

  // Event name
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(18, 18, 18);
  const nameLines = doc.splitTextToSize(event.name || "Event", textWidth);
  // Cap to 2 lines visually
  const limitedName = nameLines.slice(0, 2);
  doc.text(limitedName, bodyX, M + 12);

  // Details block (start a fixed Y so QR never collides)
  let y = M + 12 + limitedName.length * 7 + 4;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  if (event.venue) {
    const v = doc.splitTextToSize(`Venue:  ${event.venue}`, textWidth);
    doc.text(v.slice(0, 1), bodyX, y);
    y += 6;
  }
  if (event.start_at) {
    doc.text(`Start:  ${fmtDate(event.start_at)}`, bodyX, y);
    y += 6;
  }
  if (event.end_at) {
    doc.text(`End:    ${fmtDate(event.end_at)}`, bodyX, y);
    y += 6;
  }

  // Public URL (small, wrapped)
  if (publicUrl) {
    doc.setFontSize(7);
    doc.setTextColor(80, 80, 80);
    const urlLines = doc.splitTextToSize(publicUrl, textWidth);
    doc.text(urlLines.slice(0, 2), bodyX, y + 2);
  }

  // Footer note (full width below body content)
  doc.setDrawColor(18, 18, 18);
  doc.setLineWidth(0.3);
  doc.line(bodyX, PAGE_H - M - 14, PAGE_W - M - 4, PAGE_H - M - 14);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(18, 18, 18);
  doc.text(
    "Present this QR at the entrance for admission.",
    bodyX,
    PAGE_H - M - 7,
  );

  // ----- QR code -----
  const qrDataUrl = await QRCode.toDataURL(ticket.code, {
    errorCorrectionLevel: "M",
    margin: 0,
    width: 512,
    color: { dark: "#121212", light: "#FFFFFF" },
  });
  // White card behind QR with thin black border
  doc.setFillColor(255, 255, 255);
  doc.rect(qrX - 1.5, qrY - 1.5, qrSize + 3, qrSize + 3, "F");
  doc.setDrawColor(18, 18, 18);
  doc.setLineWidth(0.5);
  doc.rect(qrX - 1.5, qrY - 1.5, qrSize + 3, qrSize + 3);
  doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);

  // Code under the QR (centered)
  doc.setFont("courier", "normal");
  doc.setFontSize(9);
  doc.setTextColor(18, 18, 18);
  doc.text(ticket.code, qrX + qrSize / 2, qrY + qrSize + 6, { align: "center" });

  // (bodyW is computed but not used elsewhere; reference to avoid lint warning)
  void bodyW;
}

/** Download a single-ticket PDF. */
export async function downloadTicketPdf({ event, ticket, publicUrl }) {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: [PAGE_W, PAGE_H],
  });
  await renderTicket(doc, { event, ticket, publicUrl });
  const safeName = (event.name || "event").replace(/[^a-z0-9]+/gi, "-");
  doc.save(`${safeName}-ticket-${ticket.serial}.pdf`);
}

/** Download a multi-page PDF, one ticket per page. */
export async function downloadAllTicketsPdf({ event, tickets, makePublicUrl }) {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: [PAGE_W, PAGE_H],
  });
  for (let i = 0; i < tickets.length; i++) {
    if (i > 0) doc.addPage([PAGE_W, PAGE_H], "landscape");
    await renderTicket(doc, {
      event,
      ticket: tickets[i],
      publicUrl: makePublicUrl ? makePublicUrl(tickets[i].code) : null,
    });
  }
  const safeName = (event.name || "event").replace(/[^a-z0-9]+/gi, "-");
  doc.save(`${safeName}-all-tickets.pdf`);
}

/** Build a wa.me share URL for a ticket. */
export function whatsappShareUrl({ event, ticket, publicUrl }) {
  const lines = [
    `🎟️ Your ticket for *${event.name}*`,
    `Ticket #${ticket.serial}`,
    event.venue ? `Venue: ${event.venue}` : null,
    event.start_at ? `Start: ${fmtDate(event.start_at)}` : null,
    publicUrl ? `Open on your phone: ${publicUrl}` : null,
    `Show this at the entrance for admission.`,
  ].filter(Boolean);
  return `https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`;
}
